/**
 * @module support/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:no-any  max-file-line-count*/

import { Injectable, Injector } from '@angular/core';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';

import { FilterCriteria } from '@experience/repdash/interfaces/filter-criteria.interface';
import { Address } from '@support/api/proxies/responses/api-2004.interface';
import { SelectItem } from '@support/shared/interfaces/select-item.interface';
import { States } from '@experience/repdash/interfaces/state.interface';
import {
  Cart,
  CartItem,
  CartLine,
} from '@support/api/proxies/responses/api-cart-response.interface';

// Constants
import { TEXT_CONSTANTS, WINDOW_PRINT_CONST } from '@support/shared/constants/device.constants';
import { REPDASH_SCREENID_CONSTANTS } from '@experience/repdash/repdash.constants';
import { ORDER_TYPE, OFFER_TYPE } from '@experience/shop/shop.constants';
import { SETTINGS } from '@environment/settings';

import moment from 'moment';
import * as momentTimeZone from 'moment-timezone';
import { KeyValueMap } from '@support/shared/interfaces/key-value-map.interface';
import { lineTypeEnum } from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { MANAGE_AUTOPAY_CONSTANTS } from '@experience/manage-autopay/constant/manage-autopay-constant';
import { randomNumber as cryptoRandomNumber } from '@support/shared/services/random-number.helper';

// We are not able to get the exact type of variable so need to discuss with concerned developer

@Injectable(
  { providedIn: 'root' }
)
export class CommonUtilService {
  /**
   * Stores the property on which to sort
   */
  private property: string;

  private extImgUrl: string;

  /**
   * Creates an instance of CommonUtilService.
   * @param {Injector} [injector]
   * @memberof CommonUtilService
   */
  constructor(private injector?: Injector) {
    // @TODO - Change this image url path to AEM
    // this.extImgUrl = this.appConfig.getConfig('extTmoImagesUrl') || SETTINGS.EXT_IMAGE_URL;
    this.extImgUrl = SETTINGS.EXT_IMAGE_URL;
  }

  /**
   * Main function to sort the values
   * @param collection - the collection to be sorted
   * @param property - sorting condition
   * @param orderBy - to decide to make it ascending or descending
   * @param sortOrder - tocustom sort the collection
   * @param isNumeric - to check whether collection have numeric string
   */
  public sortUtil(
    collection: any[],
    property: string,
    orderBy: boolean,
    sortOrder?: string[],
    isNumeric?: boolean
  ): any[] {
    this.property = property;
    let output = [];
    if (sortOrder && sortOrder.length > 0) {
      const sortLen = sortOrder.length;
      for (let i: number = 0; i < sortLen; i++) {
        const sortUtilOrder: string = sortOrder[i];
        const filteredList: any[] = this.collectionFilter(collection, property, sortUtilOrder);
        if (filteredList.length > 0) {
          output = output.concat(filteredList);
        }
      }
    } else if (isNumeric) {
      output = this.sortByDigits(collection, this.property);
    } else {
      collection.sort(this.comparator.bind(this));
      output = collection;
    }
    if (!orderBy) {
      output = output.reverse();
    }
    return output;
  }

  /**
   * Function to handle null values
   * @param value - the value to check for nullability
   */
  public isNotEmpty(value: any): boolean {
    if (
      (Array.isArray(value) && value.length > 0) ||
      (!Array.isArray(value) && value !== null && value !== undefined && value !== '')
    ) {
      return !0;
    } else {
      return !1;
    }
  }

  /**
   * Function to compare and sort two key value pairs
   * @param first - the first value to compare
   * @param second - the second value to compare
   */
  public comparator(first: any, second: any): number {
    first = first[this.property].toString().toLowerCase();
    second = second[this.property].toString().toLowerCase();
    if (first > second) {
      return 1;
    }
    if (first.toString() < second.toString()) {
      return -1;
    }
    return 0;
  }

  /**
   * Main function to sort the values
   * @param collection - the collection to be sorted
   * @param property - sorting condition
   * @param orderBy - to decide to make it ascending or descending
   * @param sortOrder - tocustom sort the collection
   */
  public numericSort(collection: any[], property: string, orderBy: boolean): any[] {
    this.property = property;
    let output = [];
    collection.sort(this.numericComparator.bind(this));
    output = collection;
    if (!orderBy) {
      output = output.reverse();
    }
    return output;
  }

  /**
   * Function to compare and sort two key value pairs
   * @param first - the first value to compare
   * @param second - the second value to compare
   */
  public numericComparator(first: any, second: any): number {
    first = first[this.property];
    second = second[this.property];
    if (first > second) {
      return 1;
    }
    if (first < second) {
      return -1;
    }
    return 0;
  }

  /**
   * Function to create a filter and return searchFilter object
   * @param searchFilterObject - Object that holds search results and filter criteria
   * returns FilterCriteria
   */
  public createFilterUtil(searchFilterObject: FilterCriteria): FilterCriteria {
    searchFilterObject.searchFilteredResults = searchFilterObject.searchFilteredResults.filter(
      (item: any) => {
        for (const fieldType in searchFilterObject.filterCriteria) {
          if (
            item[fieldType] === undefined ||
            item[fieldType] !== searchFilterObject.filterCriteria[fieldType]
          ) {
            return false;
          }
        }
        return true;
      }
    );
    return searchFilterObject;
  }

  /**
   * Function to convert states for dropdown component and return stateOptions
   * @param states - the list of states to display in dropdown
   */
  public convertStatesForTmoDropDown(states: States): SelectItem[] {
    const stateOptions = [];
    const statesCount = states.states.length;
    for (let i = 0; i < statesCount; i++) {
      stateOptions.push({
        label: states.states[i].name,
        value: states.states[i].code,
      });
    }

    return stateOptions;
  }

  /**
   * Function to generate random number based on length
   * @param length: number
   */
  public generateEventId(length: number): number {
    const i = 10;
    const j = 1;
    const k = 9;
    return Math.floor(Math.pow(i, length - j) + cryptoRandomNumber() * k * Math.pow(i, length - j));
  }

  /**
   * Function to convert states for dropdown component and return stateOptions
   * @param states - the list of states to display in dropdown
   */
  public createDropDownData(inpuData: any[], labelKey?: string, valueKey?: string): SelectItem[] {
    const tmoDropDownData = [];
    const inputLen = inpuData.length;
    for (let data = 0; data <= inputLen - 1; data++) {
      tmoDropDownData.push({
        label: labelKey ? inpuData[data][labelKey] : inpuData[data],
        value: valueKey ? inpuData[data][valueKey] : inpuData[data],
      });
    }
    return tmoDropDownData;
  }

  /**
   * Function to format configurable data
   * @param message: string - configurable text
   * @param values: string[] - values need to be added in configurable text
   */
  public messageFormatter(message: string, values: string[]): string {
    const radix = 10;
    return this.isNotEmpty(message)
      ? message.replace(/({\d})/gi, (match: string) => {
          return values[parseInt(match.charAt(1), radix)];
        })
      : message;
  }

  /**
   * @param URL - Parameter to be string and expects to be Uniform Resource Locator
   * @param queryString - String value that needs to be appended to URL
   * @returns  string
   */
  public buildCustomQueryString(URL: string, queryString: string): string {
    let appendToUrl = '';
    if (this.isNotEmpty(queryString)) {
      appendToUrl = queryString;
      return URL.concat(appendToUrl);
    } else {
      return appendToUrl;
    }
  }
  /**
   * Function to get image of device
   * @param imageUrl: string - image url
   */
  public getImageUrl(imageUrl: string): string {
    if (this.isNotEmpty(imageUrl)) {
      return imageUrl.indexOf('http') === -1 ? this.extImgUrl + imageUrl : imageUrl;
    } else {
      return this.extImgUrl + TEXT_CONSTANTS.IMAGENOTFOUND;
    }
  }

  public checkDevice(): string {
    const width = this.getWindowWidth();
    let type: string;
    switch (true) {
      case width >= TEXT_CONSTANTS.BREAK_POINT_DESKTOP:
        type = TEXT_CONSTANTS.DEVICE_DESKTOP;
        break;
      case width >= TEXT_CONSTANTS.BREAK_POINT_TABLET:
        type = TEXT_CONSTANTS.DEVICE_TABLET;
        break;
      case width >= TEXT_CONSTANTS.BREAK_POINT_MOBILE:
        type = TEXT_CONSTANTS.DEVICE_MOBILE;
        break;
      default:
        type = TEXT_CONSTANTS.DEVICE_DESKTOP;
    }
    return type;
  }

  public getWindowWidth(): number {
    return window.innerWidth;
  }

  public getFilterCriteria(collection: any[], property: string): string[] {
    const filterArrayObject = {};
    const collectionCount = collection.length;
    for (let i = 0; i < collectionCount; i++) {
      if (!filterArrayObject.hasOwnProperty(collection[i][property])) {
        filterArrayObject[collection[i][property]] = 0;
      } else {
        filterArrayObject[collection[i][property]]++;
      }
    }
    return Object.keys(filterArrayObject);
  }

  /**
   * Function to return value of next month (0-11)
   * @param {date} date contains entered date
   * @return {date} Next Month
   */
  public getNextMonth(date: Date): number {
    return date.getMonth() === TEXT_CONSTANTS.noOfMonths ? 0 : date.getMonth() + 1;
  }

  /**
   * Function To get month name from month number
   * @param {number} monthNumber contains month number
   * @return {string} month name
   */
  public getMonthName(monthNumber: number): string {
    return TEXT_CONSTANTS.MONTH_NAMES[monthNumber - 1];
  }

  /**
   * Function to check validity of a credit card number and IMEI of Phone using luhn check algorithm
   * @param {string} value String contains text which needs to be validated
   * @return {boolean} returns true and false
   */
  public isValidLuhn(strToTest: string): boolean {
    if (/[^0-9-\s]+/.test(strToTest)) {
      return false;
    }
    let nCheck = 0;
    let nDigit = 0;
    let bEven = false;
    const ten = 10;
    const two = 2;
    const nine = 9;

    strToTest = strToTest.replace(/\D/g, '');
    for (let n = strToTest.length - 1; n >= 0; n--) {
      const cDigit = strToTest.charAt(n);
      nDigit = parseInt(cDigit, ten);
      if (bEven) {
        nDigit = nDigit *= two;
        if (nDigit > nine) {
          nDigit -= nine;
        }
      }
      nCheck += nDigit;
      bEven = !bEven;
    }
    return nCheck % ten === 0;
  }

  /**
   * Genarates random number based on the max length
   * @param max
   * @return random number
   */
  public getRandom(max: number): number {
    const base: number = 10;
    return Math.floor(cryptoRandomNumber() * Math.pow(base, max));
  }

  /**
   * method to get values as expected number format
   * @param event
   */
  public formatInput(event: string): string {
    if (event.match('\\d')) {
      event = event.replace(/[^0-9]/, '');
      const multiplier = 0.01;
      const two = 2;
      const val = (parseFloat(event) * multiplier).toFixed(two);
      return val;
    }
  }
  /**
   * Gets the number of digits in this number without decimals
   *
   * @param {number} num
   * @returns {number}
   * @memberof CommonUtilService
   */
  public getDigitsWithoutDecimal(num: number): number {
    return num ? num.toString().split('.')[0].length : 0;
  }

  /**
   * Used to encode string
   * @param {string} value String contains text need to encode
   * @return {string} encoded string
   */
  public getEncodeText(value: string): string {
    return value.replace(/(\\r\\n|\\n|\\r)/gm, '\n');
  }

  /**
   * Used to open print window
   * @param printContent: Header for terms and conditions
   * @param headerText: Header text
   * @param dateText: Date text
   * @return void
   */
  public printContent(html: string, headerText?: string, dateText?: string): void {
    const todayDate = moment(new Date()).format(WINDOW_PRINT_CONST.DATE_FORMAT);
    const win = window;
    const printWinindow = win.open(
      '',
      WINDOW_PRINT_CONST.WINDOW_TERGET,
      WINDOW_PRINT_CONST.WINDOW_FEATURES
    );
    printWinindow.document.open();

    let reportHtml = WINDOW_PRINT_CONST.TEMPLATE;
    if (headerText) {
      reportHtml = reportHtml.replace(WINDOW_PRINT_CONST.HEADER, headerText);
    }
    if (dateText) {
      dateText = dateText.concat(WINDOW_PRINT_CONST.COLON).concat(todayDate.toString());
      reportHtml = reportHtml.replace(WINDOW_PRINT_CONST.PRINT_DATE, dateText);
    }
    reportHtml = reportHtml.replace(WINDOW_PRINT_CONST.HTML, html);

    printWinindow.document.write(reportHtml);
    printWinindow.document.close();
  }

  // @TODO Used Injector instead of Router to fix the cyclic dependancy issue in 'aot'
  /**
   * To create router property on your service
   */
  public get router(): Router {
    return this.injector.get(Router);
  }

  // @TODO Used Injector instead of Router to fix the cyclic dependancy issue in 'aot'
  /**
   * To create router property on your service
   */
  public get activatedRoute(): ActivatedRoute {
    return this.injector.get(ActivatedRoute);
  }

  /**
   * To redirect user to given url.
   * @param url - String url of the new route
   * @param isNewTab - boolean to open in new tab or not
   * @returns void
   */
  public routeToGivenUrl(url: string, isNewTab: boolean): void {
    const externalLinkPrefix: string = 'ext:';
    if (url.indexOf('http') > -1 || url.startsWith(externalLinkPrefix)) {
      const target: string = isNewTab ? '_blank' : '_self';
      window.open(url.split(externalLinkPrefix)[1] || url, target);
    } else if (url !== '') {
      const queryParams: object = this.queryStringToJSON(url);
      let navigationExtras: NavigationExtras;
      if (queryParams) {
        navigationExtras = {
          skipLocationChange: false,
          queryParams: queryParams,
        };
      }
      this.router.navigate([this.removeQueryString(url)], navigationExtras);
    }
  }

  /**
   * Function to convert number in milliseconds to MM:SS string
   * to be used to display in HTML
   * @param {number} secondsInput
   * @returns {string}
   */
  public convertTime(secondsInput: number): string {
    const twoDigitTime: number = 10;
    const secondsPerHour: number = 3600;
    const secondsPerMinute: number = 60;
    const pad = (input: number) => {
      return input < twoDigitTime ? '0'.concat(input.toString()) : ''.concat(input.toString());
    };
    const minutes: string = pad(Math.floor((secondsInput % secondsPerHour) / secondsPerMinute));
    const seconds: string = pad(Math.floor(secondsInput % secondsPerMinute));
    return `${minutes}:${seconds}`;
  }

  /**
   * Checks whether we are in the retail channel or not
   *
   * @returns
   * @memberof CommonUtilService
   */
  // @TODO this function can be removed after complete segregation
  public isRetailChannel(): boolean {
    return false;
    // return SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL;
  }

  /**
   * Checks whether we are in the retail channel or not
   *
   * @returns
   * @memberof CommonUtilService
   */
  // @TODO this function can be removed after complete segregation
  public isNrdChannel(): boolean {
    // return SETTINGS.CHANNEL.toLowerCase() === CHANNELS.NRD;
    return false;
  }

  /**
   * Checks whether we are in the care channel or not
   * @returns boolean
   * @memberof CommonUtilService
   */
  // @TODO this function can be removed after complete segregation
  public isCareChannel(): boolean {
    // return SETTINGS.CHANNEL.toLowerCase() === CHANNELS.CARE;
    return false;
  }

  /**
   * Checks whether we are in the web channel or not
   *
   * @returns
   * @memberof CommonUtilService
   */
  // @TODO this function can be removed after complete segregation
  public isWebChannel(): boolean {
    // return SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB;
    return true;
  }
  private collectionFilter(collection: any[], property: string, sortUtilOrder: string): any {
    const filteredList: any[] = collection.filter((item: any) => {
      const result = item[property]
        ? item[property].toLowerCase() === sortUtilOrder.toLowerCase()
        : false;
      return result;
    });
    return filteredList;
  }

  private sortByDigits(collection: any[], property: string): any[] {
    collection.sort((first: any, second: any) => {
      return parseFloat(first[property]) - parseFloat(second[property]);
    });
    return collection;
  }

  /**
   * Removed pipeTransform applied on a string input
   * @param {string} msisdn
   * @returns {string}
   */
  public removePipeTransform(msisdn: string): string {
    return msisdn.replace(/\D/g, '');
  }

  /**
   * Method to generate Id
   * @returns {number}
   * @memberof CommonUtilService
   */
  public generateRandomId(): number {
    return Math.floor(cryptoRandomNumber() * new Date().getTime());
  }

  /**
   * function to create L00X series with the input lineId
   * @param {number} lineId
   * @returns {string} sample L001
   */
  public generateLineNumber(lineId: number | string = '1', type?: lineTypeEnum): string {
    let lineNumber: string;
    switch (type) {
      case lineTypeEnum.NONLINE:
        lineNumber = 'D';
        break;
      case lineTypeEnum.LINE:
        lineNumber = 'L';
        break;
      case lineTypeEnum.EXISTING:
        lineNumber = 'E';
        break;
      default:
        lineNumber = 'L';
    }
    const lineNumberLength = 4;
    if (`${lineNumber}${lineId}`.length < lineNumberLength) {
      const lineCount = lineNumberLength - `${lineNumber}${lineId}`.length;
      for (let i = 0; i <= lineCount; i++) {
        lineNumber += '0';
      }
    }

    return `${lineNumber}${lineId}`;
  }

  /**
   * Method to generate time interval
   * @returns {SelectItem[]}
   */
  public generateTimeInterval(): SelectItem[] {
    const timeInterval = TEXT_CONSTANTS.TIMEINTERVAL;
    const timeList = [];
    const timeFormat = TEXT_CONSTANTS.TIMEFORMAT;
    const time = moment().startOf('day');
    const initialHour = time.hour();
    let passedInitialHour = false;
    while (!passedInitialHour || initialHour !== time.hour()) {
      if (!passedInitialHour) {
        passedInitialHour = time.hour() !== initialHour;
      }
      timeList.push({
        label: time.format(timeFormat),
        value: time.format(timeFormat),
      });
      time.add(timeInterval, 'm');
    }
    return timeList;
  }

  /**
   * Method to generate time zone list with abbreviations
   * @returns {SelectItem[]}
   */
  public generateTimeZoneWithAbbr(): SelectItem[] {
    const timeZone = [];
    const timeZoneList = momentTimeZone.tz.names();
    timeZoneList.forEach((zone: string) => {
      const zoneAbbreviation = momentTimeZone.tz(zone).zoneAbbr();
      timeZone.push({
        label: zone.concat(' - ').concat(zoneAbbreviation),
        value: zone.concat(' - ').concat(zoneAbbreviation),
      });
    });
    return timeZone;
  }

  public groupBy(item: object[], key: string): object {
    if (key && item) {
      return item.reduce((previousValue: any[], currentValue: any[]) => {
        const value = currentValue[key];
        previousValue[value] = previousValue[value] || [];
        previousValue[value].push(currentValue);
        return previousValue;
      }, {});
    }
  }

  public groupByValues(item: object[], key: string, keyValue: string): object {
    if (key && item) {
      return item.reduce((previousValue: any[], currentValue: any[]) => {
        const value = currentValue[key];
        previousValue[value] = previousValue[value] || [];
        previousValue[value].push(currentValue[keyValue]);
        return previousValue;
      }, {});
    }
  }
  /**
   * Method to generate Array of Key Values
   * @returns {SelectItem[]}
   */
  public createKeyValueArray(keyValueObject: KeyValueMap[]): SelectItem[] {
    const selectItemArray: SelectItem[] = [];
    const keyLen = keyValueObject.length;
    for (let i = 0; i < keyLen; i++) {
      const tmpdata: SelectItem = {
        label: Object.keys(keyValueObject[i])[0],
        value: Object.values(keyValueObject[i])[0],
      };
      selectItemArray.push(tmpdata);
    }
    return selectItemArray;
  }

  /**
   * Method to get screen id of specific page
   * @returns {string} sample SC1135
   */
  public getScreenId(permissionObject: any, screenIdKey: string): string {
    screenIdKey =
      screenIdKey || (permissionObject && this.router && this.router.url)
        ? this.router.url.split('?')[0]
        : '';
    // default screen id SC1135 for all channels
    const screenId =
      permissionObject && permissionObject[screenIdKey] && permissionObject[screenIdKey].screenId
        ? permissionObject[screenIdKey].screenId
        : REPDASH_SCREENID_CONSTANTS.DEFAULT;
    return screenId;
  }

  /**
   * Method to get query params string
   * @returns all params
   */
  public getQueryParams(queryString: string): Map<string, string> {
    const queries = queryString.split('&');
    const params = new Map();
    if (queryString && queries) {
      for (let i = 0; queries.length > i; i++) {
        const rest = queries[i].substring(queries[i].indexOf('=') + 1);
        const temp = queries[i].split('=');
        params[temp[0]] = rest;
      }
    }
    return params;
  }
  /**
   * Method accepts string of query params and converts them to an object of query params
   * This method is safe to use on query params that include an additonal equals sign
   * TODO add test in spec file for this func
   * @returns all params
   */
  public getQueryParamsAsObject(queryString: string): Object {
    const paramsObject = queryString
      .slice(1)
      .split('&')
      .map((p: string) => p.split(/=(.*)[\r\n]*/gm)) // Split at first instance of =, to allow for params such as "tokenContent=="
      .reduce((obj: Object, pair: string[]) => {
        const [key, value]: string[] = pair;
        obj[key] = value;
        return obj;
      }, {});
    return paramsObject;
  }
  /**
   * Method accepts string as input and converts to uppercase string
   * @param {string} inputText
   * @returns {string}
   */
  public toUpperCase(inputText: string): string {
    if (this.isNotEmpty(inputText)) {
      return inputText.toUpperCase();
    }

    return inputText;
  }

  /**
   * Method accepts string as input and converts to lowercase string
   * @param {string} inputText
   * @returns {string}
   */
  public toLowerCase(inputText: string): string {
    if (this.isNotEmpty(inputText)) {
      return inputText.toLowerCase();
    }

    return inputText;
  }

  /**
   * Method to return 10 digit msisdn
   * @returns {string} sample 7147243235
   */
  public formatMsisdn10Digits(msisdn: string): string {
    const msisdnLength = 10;
    if (this.isNotEmpty(msisdn) && !(msisdn.length < msisdnLength)) {
      return msisdn.substr(msisdn.length - msisdnLength);
    }
    return msisdn;
  }

  /**
   * Method to return Ten digit msisdn
   * @returns {string} sample 1234567891
   */
  public formatMsisdnTenDigits(msisdn: string): string {
    const msisdnLength = 10;
    if (this.isNotEmpty(msisdn) && !(msisdn.length < msisdnLength)) {
      return msisdn.replace(/[^\d]/g, '');
    }
    return msisdn;
  }

  /**
   * This method is used to return next billing cycle date
   * @param {number} billCycleDay
   * @return {Date}
   */
  public getNextBillCycleStartDate(billCycleDay: number): Date {
    let nextBillDate;
    let nextBillMonth;
    let nextBillYear;
    const currentDate = new Date();
    if (billCycleDay <= currentDate.getDate()) {
      currentDate.setDate(billCycleDay);
      nextBillDate = this.getTwoDigitNumber(currentDate.getDate());
      nextBillMonth = this.getTwoDigitNumber(this.getNextMonth(currentDate));
      nextBillYear =
        currentDate.getMonth() === TEXT_CONSTANTS.noOfMonths
          ? currentDate.getFullYear() + 1
          : currentDate.getFullYear();
    } else {
      currentDate.setDate(billCycleDay);
      nextBillDate = this.getTwoDigitNumber(currentDate.getDate());
      nextBillMonth = this.getTwoDigitNumber(currentDate.getMonth());
      nextBillYear = currentDate.getFullYear();
    }
    return new Date(nextBillYear, nextBillMonth, nextBillDate);
  }

  /**
   * Method to check if it is a CPS flow based on cart response
   * @param cart current cart
   * @returns boolean
   */
  public isCPSFlow(cart: Cart): boolean {
    if (cart.orderType === ORDER_TYPE.MANAGERATEPLAN) {
      return true;
    } else if (cart.orderType === ORDER_TYPE.ADDONSERVICES) {
      const editedLine = cart.lines.find((line: CartLine) => line.isLineEdited);
      return editedLine ? editedLine.services[0].offerType === OFFER_TYPE.ADDON : false;
    } else {
      return false;
    }
  }

  /**
   * Method to check if it is a session pass flow based on cart response
   * @param cart current cart
   * @returns boolean
   */
  public isSessionPassFlow(cart: Cart): boolean {
    let addedService: CartItem;
    if (cart.orderType === ORDER_TYPE.ADDONSERVICES) {
      cart.lines.forEach((line: CartLine) => {
        addedService = line.services.find((service: CartItem) => {
          return service.statusCode === 'ADD';
        });
      });
      return addedService && addedService.offerType === OFFER_TYPE.ONDEMAND;
    } else {
      return false;
    }
  }

  /**
   * Function to stringify address object, to be used as value for lable in dropdowns
   * @param {Address} address
   * @returns {string}
   */
  public stringifyAddress(address: Address): string {
    const checkedAddress: Address = address && address.addressLine1 ? address : {};
    const urbanizationCode = address && address.urbanization ? `${address.urbanization}, ` : '';
    const addressLine2 = address && address.addressLine2 ? address.addressLine2 : '';
    return `${urbanizationCode}${(checkedAddress.addressLine1 || '').replace(
      /\s+/g,
      ''
    )},${addressLine2.replace(/\s+/g, '')},${checkedAddress.cityName},${
      checkedAddress.postalCode
    },${checkedAddress.stateCode}`;
  }

  /**
   * Formats the date
   * @param date
   */
  public formatDate(date: string): string {
    const inputDate = moment(new Date(date))
      .format(MANAGE_AUTOPAY_CONSTANTS.DATE_FORMAT_YYYYMMDD)
      .toString();
    return inputDate + MANAGE_AUTOPAY_CONSTANTS.DEFAULT_TIME;
  }

  /**
   * Formats the date
   * @param date
   */
  public formatDateWithoutZone(date: Date): Date {
    const newDate = moment(date).format(MANAGE_AUTOPAY_CONSTANTS.DATE_FORMAT_YYYYMMDD);
    return new Date(newDate + MANAGE_AUTOPAY_CONSTANTS.DEFAULT_TIME_NO_ZONE);
  }
  /**
   * downloads pdf
   * @param content
   */
  public downloadPdf(id: string, content: string, preventSuffix?: boolean): void {
    const docId = id && id.trim() !== '' ? id : 'Customer';
    const fileName = docId + (!!preventSuffix ? '' : WINDOW_PRINT_CONST.PDF_FILE_NAME);
    //  ** IE **
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      const byteCharacters = atob(content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: WINDOW_PRINT_CONST.LINK_SOURCE_PREFIX_IE });
      window.navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      // ** Other browsers **
      const linkSource = WINDOW_PRINT_CONST.LINK_SOURCE_PREFIX + content;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    }
  }

  /**
   * Function to return path variable
   * @returns {string}
   */
  public returnPathParamFromUrl(): string {
    const pathname = window.location.pathname;
    if (pathname) {
      const paths = pathname.split('/');
      if (paths && paths.length > 1 && paths[1] !== '') {
        return paths[1];
      }
    }
    return '';
  }

  public deepEqual(object1: Object, object2: Object): boolean {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      const val1 = object1[key] ? object1[key] : '';
      const val2 = object2[key] ? object2[key] : '';
      const areObjects = this.isObject(val1) && this.isObject(val2);
      if (
        (areObjects && !this.deepEqual(val1, val2)) ||
        (!areObjects && val1.toLocaleLowerCase() !== val2.toLocaleLowerCase())
      ) {
        return false;
      }
    }

    return true;
  }

  /**
   * To check if auth code is valid or not through regex
   * @param {string} authcode
   * @returns boolean
   */
  public checkAuthCodeRegex(authcode: string, pattern?: RegExp): boolean {
    const isNotRepeatedChar = pattern ? !pattern.test(authcode) : false;
    const SECOND_POSITION = 2;
    const THIRD_POSITION = 3;
    const FOURTH_POSITION = 4;
    if (isNotRepeatedChar) {
      for (let i = 0; i < THIRD_POSITION; i++) {
        const check = authcode.substring(i, i + FOURTH_POSITION).toLowerCase();
        const c1 = check.substring(0, 1);
        const c2 = check.substring(1, SECOND_POSITION);
        const c3 = check.substring(SECOND_POSITION, THIRD_POSITION);
        const c4 = check.substring(THIRD_POSITION, FOURTH_POSITION);
        if (
          (c1.charCodeAt(0) + 1 === c2.charCodeAt(0) ||
            c1.charCodeAt(0) - 1 === c2.charCodeAt(0)) &&
          (c1.charCodeAt(0) + SECOND_POSITION === c3.charCodeAt(0) ||
            c1.charCodeAt(0) - SECOND_POSITION === c3.charCodeAt(0)) &&
          (c1.charCodeAt(0) + THIRD_POSITION === c4.charCodeAt(0) ||
            c1.charCodeAt(0) - THIRD_POSITION === c4.charCodeAt(0))
        ) {
          return false;
        }
      }
      return true;
    } else {
      return false;
    }
  }

  /**
   * To get double digit number in case of single digit
   * @param {number} n
   * @return {string}
   */
  private getTwoDigitNumber(n: number): string {
    return n < TEXT_CONSTANTS.noOfDigits ? '0' + n.toString() : '' + n.toString();
  }

  /**
   * To get query string url as object
   * @param {string} url
   * @return {object}
   */
  private queryStringToJSON(url: string): object {
    if (url) {
      const urlArray: string[] = url.split('?');
      if (urlArray.length > 1) {
        const queryString: string = urlArray[1];
        if (queryString) {
          const pairs: string[] = queryString.split('&');
          const result: object = {};
          pairs.forEach((pairString: string) => {
            const pair: string[] = pairString.split('=');
            if (pair && pair[0]) {
              result[pair[0]] = decodeURIComponent(pair[1] || '');
            }
          });
          return result;
        }
      }
    }
  }

  /**
   * To get query string url as object
   * @param {string} url
   * @return {string}
   */
  private removeQueryString(url: string): string {
    if (url) {
      const urlArray: string[] = url.split('?');
      return urlArray[0];
    } else {
      return '';
    }
  }

  /**
   * Method to get concat error and errorcode
   * @param {string} error
   * @param {string} errorcode
   * @return {string}
   */
  public getErrorMessageWithCode(error: string, errorCode: string): string {
    // return this.isCareChannel() || this.isRetailChannel() ? `${error} ${errorCode}` : error;
    return error;
  }

  private isObject(object: Object): boolean {
    return object !== null && typeof object === 'object';
  }

  /**
   * Copies input to Clipboard
   * @param input
   */
  public copyToClipboard(input: string): void {
    const elementValue = input;
    const copyTextInput = document.createElement('input');
    copyTextInput.setAttribute('type', 'text');
    copyTextInput.setAttribute('value', elementValue);
    document.body.appendChild(copyTextInput);
    copyTextInput.select();
    document.execCommand('copy');
    document.body.removeChild(copyTextInput);
  }

  /**
   * Method to check if the value is Numeric
   * @param value
   */
  isNumeric(value: any): boolean {
    return !isNaN(value - parseFloat(value));
  }
}
